import React, { useEffect, useState, useRef } from "react";
import config from "../../constants/config";

const { ENV_SUFIX } = config;
const CDC_HOST = process.env[`REACT_APP_CDC_HOST_NAME${ENV_SUFIX}`];
const CDC_API_KEY = process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`];

/**
 * Logout — React equivalent of the original logout.html
 *
 * Flow:
 * 1. Set dynamic favicon based on ReturnURL query param
 * 2. Determine spinner theme (fishersci vs default) from ReturnURL
 * 3. Load gigya.js, auditSupport.js, angular.1.1.5.js, logout-min.js
 * 4. Show loading spinner until logout-min.js handles the redirect
 */
function Logout() {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // ─── Step 1: Dynamic favicon based on ReturnURL ───────────────────────
    try {
      const urlParams = new URL(window.location.href).searchParams;
      const returnUrlParam = urlParams.get("ReturnURL");

      const link = document.createElement("link");
      link.rel = "icon";

      if (returnUrlParam) {
        const returnUrlOrigin = new URL(returnUrlParam).origin;
        link.href = `${returnUrlOrigin}/favicon.ico`;
      } else {
        link.href = "/favicon.ico";
      }

      document.head.appendChild(link);
    } catch (e) {
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = "/favicon.ico";
      document.head.appendChild(link);
      console.warn("Failed to set dynamic favicon:", e);
    }

    // ─── Step 3: Load scripts in order ────────────────────────────────────
    // gigya.js — auditSupport.js and logout-min.js are chained in onload below
    const gigyaScript = document.createElement("script");
    gigyaScript.type = "text/javascript";
    gigyaScript.src = `https://${CDC_HOST}/js/gigya.js?apiKey=${CDC_API_KEY}`;
    document.head.appendChild(gigyaScript);

    // angular.1.1.5.js
    // const angularScript = document.createElement("script");
    // angularScript.type = "text/javascript";
    // angularScript.src = "/identity/shared-static/scripts/angular.1.1.5.js";
    // document.body.appendChild(angularScript);

    // logout-min.js (depends on both gigya and auditSupport — load in sequence)
    gigyaScript.onload = () => {
      const auditScript = document.createElement("script");
      auditScript.type = "text/javascript";
      auditScript.src = "/identity/static/js/tfidentity/auditSupport.js";

      const loadAndInitLogout = () => {
        const logoutScript = document.createElement("script");
        logoutScript.type = "text/javascript";
        logoutScript.src = "/account-center/scripts/logout-min.js";
        logoutScript.onload = () => {
          if (
            window?.logoutApp &&
            typeof window?.logoutApp.initLogout === "function"
          ) {
            window.logoutApp.initLogout();
          } else if (
            window?.logoutModule &&
            typeof window?.logoutModule.initLogout === "function"
          ) {
            window.logoutModule.initLogout();
          } else {
            console.error(
              "window.logoutApp.initLogout is not available after logout-min.js loaded",
            );
          }
        };
        logoutScript.onerror = () => {
          console.error("logout-min.js failed to load");
        };
        document.body.appendChild(logoutScript);
      };

      auditScript.onload = () => {
        loadAndInitLogout();
      };

      auditScript.onerror = () => {
        console.warn(
          "auditSupport.js failed to load — logAuditData will be unavailable",
        );
        loadAndInitLogout();
      };

      document.head.appendChild(auditScript);
    };
  }, []);

  useEffect(() => {
    window?.tfIdentityGlobals?.showLoader();
  }, []);

  return (
    null
  );
}

export default Logout;
